import { ViewQueueTypes } from './types';

const initialState = {
  userQueues: [],
  publicQueues: [],
  selectedQueue: null,
  publicViewedQueue: null,
  queueOwner: null,
  isQueueOwner: false,
  loading: {
    userQueues: false,
    publicQueues: false,
    publicQueue: false,
    create: false,
    update: false,
    delete: false,
    addVideo: false,
    removeVideo: false,
    reorder: false,
    queuePlayback: false,
    watchlist: false
  },
  error: {
    userQueues: null,
    publicQueues: null,
    publicQueue: null,
    create: null,
    update: null,
    delete: null,
    addVideo: null,
    removeVideo: null,
    reorder: null,
    queuePlayback: null,
    watchlist: null
  },
  search: {
    results: [],
    loading: false,
    error: null,
    filters: {
      query: '',
      page: 1,
      limit: 20,
      viewMode: 'grid'
    },
    totalResults: 0,
    currentPage: 1,
    totalPages: 1
  },
  player: {
    currentQueue: null,
    currentVideoIndex: 0,
    queueVideos: [],
    isPlaying: false,
    autoplayEnabled: true
  },
  ui: {
    activeTab: 'my-queues', // 'my-queues', 'discover', 'watch'
    isCreateModalOpen: false,
    isEditModalOpen: false
  }
};

export const viewQueueReducer = (state = initialState, action) => {
  switch (action.type) {
    // Fetch user queues
    case ViewQueueTypes.FETCH_USER_QUEUES_REQUEST:
      return {
        ...state,
        loading: {
          ...state.loading,
          userQueues: true
        },
        error: {
          ...state.error,
          userQueues: null
        }
      };
      
    case ViewQueueTypes.FETCH_USER_QUEUES_SUCCESS:
      return {
        ...state,
        userQueues: action.payload,
        loading: {
          ...state.loading,
          userQueues: false
        }
      };
      
    case ViewQueueTypes.FETCH_USER_QUEUES_FAILURE:
      return {
        ...state,
        loading: {
          ...state.loading,
          userQueues: false
        },
        error: {
          ...state.error,
          userQueues: action.payload
        }
      };
    
    // Create queue
    case ViewQueueTypes.CREATE_QUEUE_REQUEST:
      return {
        ...state,
        loading: {
          ...state.loading,
          create: true
        },
        error: {
          ...state.error,
          create: null
        }
      };
      
    case ViewQueueTypes.CREATE_QUEUE_SUCCESS:
      return {
        ...state,
        userQueues: [...state.userQueues, action.payload],
        loading: {
          ...state.loading,
          create: false
        },
        ui: {
          ...state.ui,
          isCreateModalOpen: false
        }
      };
      
    case ViewQueueTypes.CREATE_QUEUE_FAILURE:
      return {
        ...state,
        loading: {
          ...state.loading,
          create: false
        },
        error: {
          ...state.error,
          create: action.payload
        }
      };
    
    // Update queue
    case ViewQueueTypes.UPDATE_QUEUE_REQUEST:
      return {
        ...state,
        loading: {
          ...state.loading,
          update: true
        },
        error: {
          ...state.error,
          update: null
        }
      };
      
    case ViewQueueTypes.UPDATE_QUEUE_SUCCESS:
      return {
        ...state,
        userQueues: state.userQueues.map(queue => 
          queue.viewQueueID === action.payload.viewQueueID ? action.payload : queue
        ),
        loading: {
          ...state.loading,
          update: false
        },
        ui: {
          ...state.ui,
          isEditModalOpen: false
        }
      };
      
    case ViewQueueTypes.UPDATE_QUEUE_FAILURE:
      return {
        ...state,
        loading: {
          ...state.loading,
          update: false
        },
        error: {
          ...state.error,
          update: action.payload
        }
      };
    
    // Delete queue
    case ViewQueueTypes.DELETE_QUEUE_REQUEST:
      return {
        ...state,
        loading: {
          ...state.loading,
          delete: true
        },
        error: {
          ...state.error,
          delete: null
        }
      };
      
    case ViewQueueTypes.DELETE_QUEUE_SUCCESS:
      return {
        ...state,
        userQueues: state.userQueues.filter(queue => queue.viewQueueID !== action.payload),
        loading: {
          ...state.loading,
          delete: false
        }
      };
      
    case ViewQueueTypes.DELETE_QUEUE_FAILURE:
      return {
        ...state,
        loading: {
          ...state.loading,
          delete: false
        },
        error: {
          ...state.error,
          delete: action.payload
        }
      };
    
    // Add video to queue
    case ViewQueueTypes.ADD_VIDEO_TO_QUEUE_REQUEST:
      return {
        ...state,
        loading: {
          ...state.loading,
          addVideo: true
        },
        error: {
          ...state.error,
          addVideo: null
        }
      };
      
    case ViewQueueTypes.ADD_VIDEO_TO_QUEUE_SUCCESS:
      return {
        ...state,
        userQueues: state.userQueues.map(queue => {
          if (queue.viewQueueID === action.payload.queueId) {
            return {
              ...queue,
              videos: [...queue.videos, action.payload.video]
            };
          }
          return queue;
        }),
        loading: {
          ...state.loading,
          addVideo: false
        }
      };
      
    case ViewQueueTypes.ADD_VIDEO_TO_QUEUE_FAILURE:
      return {
        ...state,
        loading: {
          ...state.loading,
          addVideo: false
        },
        error: {
          ...state.error,
          addVideo: action.payload.error
        }
      };
    
    // Watchlist actions
    case ViewQueueTypes.ADD_TO_WATCHLIST_REQUEST:
      return {
        ...state,
        loading: {
          ...state.loading,
          watchlist: true
        },
        error: {
          ...state.error,
          watchlist: null
        }
      };
      
    case ViewQueueTypes.ADD_TO_WATCHLIST_SUCCESS:
      // No need to modify state here as ADD_VIDEO_TO_QUEUE_SUCCESS 
      // has already updated the queue in state
      return {
        ...state,
        loading: {
          ...state.loading,
          watchlist: false
        }
      };
      
    case ViewQueueTypes.ADD_TO_WATCHLIST_FAILURE:
      return {
        ...state,
        loading: {
          ...state.loading,
          watchlist: false
        },
        error: {
          ...state.error,
          watchlist: action.payload
        }
      };
    
    // Remove video from queue
    case ViewQueueTypes.REMOVE_VIDEO_FROM_QUEUE_REQUEST:
      return {
        ...state,
        loading: {
          ...state.loading,
          removeVideo: true
        },
        error: {
          ...state.error,
          removeVideo: null
        }
      };
      
    case ViewQueueTypes.REMOVE_VIDEO_FROM_QUEUE_SUCCESS:
      return {
        ...state,
        userQueues: state.userQueues.map(queue => {
          if (queue.viewQueueID === action.payload.queueId) {
            return {
              ...queue,
              videos: queue.videos.filter(video => video.videoId !== action.payload.videoId)
            };
          }
          return queue;
        }),
        loading: {
          ...state.loading,
          removeVideo: false
        }
      };
      
    case ViewQueueTypes.REMOVE_VIDEO_FROM_QUEUE_FAILURE:
      return {
        ...state,
        loading: {
          ...state.loading,
          removeVideo: false
        },
        error: {
          ...state.error,
          removeVideo: action.payload.error
        }
      };
    
    // Reorder videos in queue
    case ViewQueueTypes.REORDER_QUEUE_VIDEOS_REQUEST:
      return {
        ...state,
        loading: {
          ...state.loading,
          reorder: true
        },
        error: {
          ...state.error,
          reorder: null
        }
      };
      
    case ViewQueueTypes.REORDER_QUEUE_VIDEOS_SUCCESS:
      return {
        ...state,
        userQueues: state.userQueues.map(queue => {
          if (queue.viewQueueID === action.payload.queueId) {
            const videoPositions = action.payload.videoPositions;
            
            // Create a new videos array with updated positions
            const updatedVideos = [...queue.videos];
            updatedVideos.forEach(video => {
              if (videoPositions[video.videoId] !== undefined) {
                video.position = videoPositions[video.videoId];
              }
            });
            
            // Sort videos by position
            updatedVideos.sort((a, b) => a.position - b.position);
            
            return {
              ...queue,
              videos: updatedVideos
            };
          }
          return queue;
        }),
        loading: {
          ...state.loading,
          reorder: false
        }
      };
      
    case ViewQueueTypes.REORDER_QUEUE_VIDEOS_FAILURE:
      return {
        ...state,
        loading: {
          ...state.loading,
          reorder: false
        },
        error: {
          ...state.error,
          reorder: action.payload.error
        }
      };
    
    // Search public queues
    case ViewQueueTypes.SEARCH_QUEUES_REQUEST:
      return {
        ...state,
        search: {
          ...state.search,
          loading: true,
          error: null
        }
      };
      
    case ViewQueueTypes.SEARCH_QUEUES_SUCCESS:
      return {
        ...state,
        publicQueues: action.payload.queues,
        search: {
          ...state.search,
          loading: false,
          results: action.payload.queues,
          totalResults: action.payload.totalResults,
          currentPage: action.payload.currentPage,
          totalPages: action.payload.totalPages
        }
      };
      
    case ViewQueueTypes.SEARCH_QUEUES_FAILURE:
      return {
        ...state,
        search: {
          ...state.search,
          loading: false,
          error: action.payload
        }
      };
    
    // Queue player actions
    case ViewQueueTypes.LOAD_QUEUE_FOR_PLAYBACK_REQUEST:
      return {
        ...state,
        loading: {
          ...state.loading,
          queuePlayback: true
        },
        error: {
          ...state.error,
          queuePlayback: null
        }
      };
      
    case ViewQueueTypes.LOAD_QUEUE_FOR_PLAYBACK_SUCCESS:
      return {
        ...state,
        player: {
          ...state.player,
          currentQueue: action.payload.queue,
          queueVideos: action.payload.videos,
          currentVideoIndex: action.payload.startIndex,
          isPlaying: true
        },
        loading: {
          ...state.loading,
          queuePlayback: false
        }
      };
      
    case ViewQueueTypes.LOAD_QUEUE_FOR_PLAYBACK_FAILURE:
      return {
        ...state,
        loading: {
          ...state.loading,
          queuePlayback: false
        },
        error: {
          ...state.error,
          queuePlayback: action.payload
        }
      };
      
    case ViewQueueTypes.SET_QUEUE_PLAYBACK_INDEX:
      return {
        ...state,
        player: {
          ...state.player,
          currentVideoIndex: action.payload,
          isPlaying: true
        }
      };
      
    case ViewQueueTypes.NEXT_QUEUE_VIDEO:
      const nextIndex = state.player.currentVideoIndex + 1;
      // Don't go past the end of the queue
      if (nextIndex >= state.player.queueVideos.length) {
        return state; // Stay at the end
      }
      return {
        ...state,
        player: {
          ...state.player,
          currentVideoIndex: nextIndex,
          isPlaying: true
        }
      };
      
    case ViewQueueTypes.PREVIOUS_QUEUE_VIDEO:
      const prevIndex = state.player.currentVideoIndex - 1;
      // Don't go below 0
      if (prevIndex < 0) {
        return state; // Stay at the beginning
      }
      return {
        ...state,
        player: {
          ...state.player,
          currentVideoIndex: prevIndex,
          isPlaying: true
        }
      };
      
    case ViewQueueTypes.TOGGLE_QUEUE_AUTOPLAY:
      return {
        ...state,
        player: {
          ...state.player,
          autoplayEnabled: action.payload !== undefined ? action.payload : !state.player.autoplayEnabled
        }
      };
      
    case ViewQueueTypes.CLEAR_QUEUE_PLAYBACK:
      return {
        ...state,
        player: {
          ...state.player,
          currentQueue: null,
          queueVideos: [],
          currentVideoIndex: 0,
          isPlaying: false
        }
      };
      
    case ViewQueueTypes.UPDATE_QUEUE_TIMESTAMP:
      return {
        ...state,
        player: {
          ...state.player,
          queueVideos: state.player.queueVideos.map(video => {
            // Match by videoId or id
            if ((video.videoId && video.videoId === action.payload.videoId) || 
                (video.id && video.id === action.payload.videoId)) {
              return {
                ...video,
                timestamp: action.payload.timestamp
              };
            }
            return video;
          })
        }
      };
      
    // Fetch single public queue (guest-safe)
    case ViewQueueTypes.FETCH_PUBLIC_QUEUE_REQUEST:
      return {
        ...state,
        publicViewedQueue: null,
        queueOwner: null,
        isQueueOwner: false,
        loading: {
          ...state.loading,
          publicQueue: true
        },
        error: {
          ...state.error,
          publicQueue: null
        }
      };
      
    case ViewQueueTypes.FETCH_PUBLIC_QUEUE_SUCCESS:
      return {
        ...state,
        publicViewedQueue: action.payload.queue,
        queueOwner: action.payload.owner,
        isQueueOwner: action.payload.isOwner,
        selectedQueue: action.payload.queue,
        loading: {
          ...state.loading,
          publicQueue: false
        }
      };
      
    case ViewQueueTypes.FETCH_PUBLIC_QUEUE_FAILURE:
      return {
        ...state,
        loading: {
          ...state.loading,
          publicQueue: false
        },
        error: {
          ...state.error,
          publicQueue: action.payload
        }
      };
    
    // UI state actions
    case ViewQueueTypes.SET_SELECTED_QUEUE:
      return {
        ...state,
        selectedQueue: action.payload
      };
      
    case ViewQueueTypes.CLEAR_SELECTED_QUEUE:
      return {
        ...state,
        selectedQueue: null
      };
      
    case ViewQueueTypes.UPDATE_QUEUE_FILTERS:
      return {
        ...state,
        search: {
          ...state.search,
          filters: {
            ...state.search.filters,
            ...action.payload
          }
        }
      };
      
    case ViewQueueTypes.SET_QUEUE_VIEW_MODE:
      return {
        ...state,
        search: {
          ...state.search,
          filters: {
            ...state.search.filters,
            viewMode: action.payload
          }
        }
      };
      
    case ViewQueueTypes.SET_ACTIVE_TAB:
      return {
        ...state,
        ui: {
          ...state.ui,
          activeTab: action.payload
        }
      };
      
    // Toggle create modal visibility and optionally clear edit state
    // This ensures the modal always opens in "create mode" when coming from the "New Queue" button
    case ViewQueueTypes.TOGGLE_CREATE_MODAL:
      const payload = typeof action.payload === 'object' ? action.payload : { isOpen: action.payload };
      return {
        ...state,
        ui: {
          ...state.ui,
          isCreateModalOpen: payload.isOpen !== undefined ? payload.isOpen : !state.ui.isCreateModalOpen,
          // Clear queueToEdit when specified to ensure modal opens in create mode
          queueToEdit: payload.clearEditState ? null : state.ui.queueToEdit
        }
      };
      
    case ViewQueueTypes.TOGGLE_EDIT_MODAL:
      return {
        ...state,
        ui: {
          ...state.ui,
          isEditModalOpen: action.payload.isOpen,
          // If a queue is provided, use it as the selected queue
          ...(action.payload.queue && { queueToEdit: action.payload.queue })
        },
        // Ensure we have a selected queue if one was provided
        ...(action.payload.queue && { selectedQueue: action.payload.queue })
      };
    
    default:
      return state;
  }
};